import { useEffect, useRef, useState } from "react";
import { TbEdit } from "react-icons/tb";
import { FaLocationDot } from "react-icons/fa6";
import FilePreview from "../renderFilePreview";
import TextFieldCustom from "../elements/textfieldCustom";
import CustomButton from "../elements/CustomButton/CustomButton";
import { useSnackbar } from "../SnackbarContext";
import { validateForm } from "../../utils/formValidation/validation";
import { getLatLngFromZip, reverseGeocode } from "../../utils/latLongFetch";
import { buildShortStorePayload } from "../../utils/buildUserPayload";
import UploadCard from "../uploadCard";
import MapComponent from "../mapComponent";
import axiosInstance from "../../configuration/request";
import { storeDetailsRules } from "../../utils/formValidation/validationRules";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const documents = [
  {
    heading: "Upload Industry License",
    documentType: "PDF, JPEG or JPG",
    name: "industry_license",
  },
  {
    heading: "Upload Shop Image",
    documentType: "PDF, JPEG or JPG",
    name: "shop_image",
  },
];

export default function StoreSection({ storeDetails, onSave }) {
  const showSnackbar = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [missingDocs, setMissingDocs] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const requiredDocs = ["industry_license", "shop_image"];
  const [initialFormData, setInitialFormData] = useState({});
  const [initialUploadedFiles, setInitialUploadedFiles] = useState([]);

  const [formData, setFormData] = useState({
    storeName: "",
    storeContactNumber: "",
    storeLicenseNumber: "",
    addressLine1: "",
    addressLine2: "",
    landmark: "",
    city: "",
    state: "",
    zip: "",
    latitude: null,
    longitude: null,
  });
  const [isMapOpen, setIsMapOpen] = useState(false);
  const formDataWhenMapOpenedRef = useRef({
    latitude: null,
    longitude: null,
    addressLine1: "",
    landmark: "",
    city: "",
    state: "",
    zip: "",
  });

  useEffect(() => {
    if (storeDetails) {
      const addr = storeDetails.storeAddresses?.[0];
      const latlong = addr?.latlongs?.[0];
      const initialData = {
        storeName: storeDetails.storeName || "",
        storeContactNumber: storeDetails.store_phone || "",
        storeLicenseNumber: storeDetails.licenceNumber || "",
        addressLine1: addr?.house_no || "",
        addressLine2: addr?.street_address || "",
        landmark: addr?.landmark || "",
        city: addr?.city || "",
        state: addr?.state || "",
        zip: addr?.zipcode || "",
        latitude: latlong?.latitude ?? null,
        longitude: latlong?.longitude ?? null,
      };
      setFormData(initialData);
      setInitialFormData(initialData);

      const initialDocs = storeDetails.uploadDoc ?? [];
      setUploadedFiles(initialDocs);
      setInitialUploadedFiles(initialDocs);
    }
  }, [storeDetails]);

  const handleChange = (field) => (e) => {
    const value = e.target?.value ?? e;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileSelect = async (file, fileName) => {
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await axiosInstance.post("/customer/upload/file", form);
      const fileUrl = response.data?.url || "";

      setUploadedFiles((prev) => {
        const filtered = prev.filter((doc) => doc.name !== fileName);
        return [...filtered, { name: fileName, fileUrl }];
      });
      setMissingDocs((prev) => prev.filter((doc) => doc !== fileName));
    } catch (error) {
      console.error("Upload failed:", error);
      showSnackbar("File upload failed", "error");
    }
  };

  const handleSave = async () => {
    const errors = validateForm(formData, storeDetailsRules);
    setFormErrors(errors);
    const uploadedNames = uploadedFiles.map((f) => f.name);
    const missing = requiredDocs.filter((doc) => !uploadedNames.includes(doc));
    setMissingDocs(missing);

    if (Object.keys(errors).length === 0 && missing.length === 0) {
      let updatedForm = { ...formData };
      const address = storeDetails?.storeAddresses?.[0];
      const existingLatlong = address?.latlongs?.[0];

      const hasMapPickedLocation =
        formData.latitude != null && formData.longitude != null;
      const needsUpdateLocation =
        !existingLatlong?.latitude ||
        !existingLatlong?.longitude ||
        address?.zipcode !== formData.zip;

      if (hasMapPickedLocation) {
        updatedForm.latitude = formData.latitude;
        updatedForm.longitude = formData.longitude;
        updatedForm.address_id = address?.id;
        updatedForm.latlong_id = existingLatlong?.id ?? undefined;
        updatedForm.store_id = storeDetails?.id;
      } else if (needsUpdateLocation) {
        const latlong = await getLatLngFromZip(formData.zip);
        if (!latlong) {
          showSnackbar("Failed to get location from zip code", "error");
          return;
        }
        updatedForm.latitude = latlong.latitude;
        updatedForm.longitude = latlong.longitude;
        updatedForm.address_id = address?.id;
        updatedForm.store_id = storeDetails?.id;
      } else {
        updatedForm.latitude = existingLatlong?.latitude;
        updatedForm.longitude = existingLatlong?.longitude;
        updatedForm.address_id = address?.id;
        updatedForm.latlong_id = existingLatlong?.id;
        updatedForm.store_id = storeDetails?.id;
      }
      updatedForm.uploadedDocs = uploadedFiles;
      const payload = buildShortStorePayload(updatedForm);
      if (onSave) onSave(payload);
      setIsEditing(false);
    } else {
      showSnackbar("Please fix errors and upload required docs", "error");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div className="font-semibold text-lg">Store Details</div>
        <div className="flex gap-2">
          <CustomButton
            onClick={() => {
              if (isEditing) {
                // If we are cancelling, re-initialize the form data
                setFormData(initialFormData);
                setUploadedFiles(initialUploadedFiles);
                setIsMapOpen(false);
              }
              setIsEditing(!isEditing);
            }}
            customeClass="primary-custom-button w-[100px] h-[35px]"
          >
            <span className="mr-1 text-xl">{isEditing ? "" : <TbEdit />}</span>
            <span className="text-base">{isEditing ? "Cancel" : "Edit"}</span>
          </CustomButton>
          {isEditing && (
            <CustomButton
              onClick={handleSave}
              customeClass="gradient-button w-[100px] h-[35px]"
            >
              Save
            </CustomButton>
          )}
        </div>
      </div>

      {isEditing ? (
        <>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { field: "storeName", label: "Store Name*" },
              { field: "storeContactNumber", label: "Store Contact no.*" },
              { field: "storeLicenseNumber", label: "EIN*" },
            ].map(({ field, label }) => (
              <>
                {field === "storeContactNumber" ? (
                  <div>
                    <PhoneInput
                      country={"us"}
                      value={formData.storeContactNumber}
                      onChange={(val) =>
                        handleChange("storeContactNumber")(val)
                      }
                          inputClass="!w-full !h-[53px] !border !border-[#d1d5db] !rounded-[10px]"
                      containerClass="w-full"
                      disabled={!isEditing}
                    />
                    {formErrors.storeContactNumber && (
                      <div className="text-red-600 text-xs mt-1">
                        {formErrors.storeContactNumber}
                      </div>
                    )}
                  </div>
                ) : (
                  <TextFieldCustom
                    key={field}
                    label={label}
                    fullWidth
                    value={formData[field]}
                    onChange={handleChange(field)}
                    error={!!formErrors[field]}
                    helperText={formErrors[field] || " "}
                  />
                )}
              </>
            ))}
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-700 mb-3">
              Address Details
            </div>

            <div className="mt-2">
              <TextFieldCustom
                label="Address*"
                fullWidth
                value={formData.addressLine1}
                onChange={handleChange("addressLine1")}
                error={!!formErrors.addressLine1}
                helperText={formErrors.addressLine1 || " "}
                heightClass="custom-textarea-height"
                multiline
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-0 gap-x-3">
              <TextFieldCustom
                label="Unit"
                fullWidth
                value={formData.landmark}
                onChange={handleChange("landmark")}
              />

              <TextFieldCustom
                label="City/Town*"
                fullWidth
                value={formData.city}
                onChange={handleChange("city")}
                error={!!formErrors.city}
                helperText={formErrors.city || " "}
              />

              <TextFieldCustom
                label="State*"
                fullWidth
                value={formData.state}
                onChange={handleChange("state")}
                error={!!formErrors.state}
                helperText={formErrors.state || " "}
              />

              <TextFieldCustom
                label="Zip code*"
                fullWidth
                value={formData.zip}
                onChange={handleChange("zip")}
                error={!!formErrors.zip}
                helperText={formErrors.zip || " "}
              />
            </div>

            <div className="flex items-center gap-3 mt-3">
              <button
                type="button"
                onClick={() => {
                  formDataWhenMapOpenedRef.current = {
                    latitude: formData.latitude,
                    longitude: formData.longitude,
                    addressLine1: formData.addressLine1 ?? "",
                    landmark: formData.landmark ?? "",
                    city: formData.city ?? "",
                    state: formData.state ?? "",
                    zip: formData.zip ?? "",
                  };
                  setIsMapOpen(true);
                }}
                className="text-[#1876A9] hover:text-[#02537F] hover:underline inline-flex items-center gap-2 text-sm font-medium"
              >
                <FaLocationDot className="text-lg" /> Pin location on map
              </button>
              {formData.latitude != null && formData.longitude != null && (
                <span className="text-xs text-gray-500">
                  Location set ({Number(formData.latitude).toFixed(5)}, {Number(formData.longitude).toFixed(5)})
                </span>
              )}
            </div>
            {isMapOpen && (
              <div className="mt-3 rounded-lg overflow-hidden border border-gray-200">
                <div className="flex justify-between items-center px-2 py-1.5 bg-gray-50 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Click on the map to set pin, then confirm or close</span>
                  <button
                    type="button"
                    onClick={() => {
                      const initial = formDataWhenMapOpenedRef.current;
                      setFormData((prev) => ({
                        ...prev,
                        latitude: initial.latitude,
                        longitude: initial.longitude,
                        addressLine1: initial.addressLine1,
                        landmark: initial.landmark,
                        city: initial.city,
                        state: initial.state,
                        zip: initial.zip,
                      }));
                      setIsMapOpen(false);
                    }}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded"
                    aria-label="Close map"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
                <MapComponent
                  isOpen={isMapOpen}
                  address={[formData.addressLine1, formData.city, formData.state, formData.zip].filter(Boolean).join(", ")}
                  onSelect={async (coords) => {
                    const address = await reverseGeocode(coords.lat, coords.lng);
                    setFormData((prev) => ({
                      ...prev,
                      latitude: coords.lat,
                      longitude: coords.lng,
                      ...(address && {
                        addressLine1: address.addressLine1 ?? prev.addressLine1,
                        landmark: address.landmark ?? prev.landmark,
                        city: address.city ?? prev.city,
                        state: address.state ?? prev.state,
                        zip: address.zip ?? prev.zip,
                      }),
                    }));
                  }}
                  height="320px"
                />
                <div className="flex justify-end gap-2 p-2 bg-gray-50">
                  <CustomButton
                    onClick={() => {
                      const initial = formDataWhenMapOpenedRef.current;
                      setFormData((prev) => ({
                        ...prev,
                        latitude: initial.latitude,
                        longitude: initial.longitude,
                        addressLine1: initial.addressLine1,
                        landmark: initial.landmark,
                        city: initial.city,
                        state: initial.state,
                        zip: initial.zip,
                      }));
                      setIsMapOpen(false);
                    }}
                    customeClass="primary-custom-button h-[36px] px-3"
                  >
                    Close
                  </CustomButton>
                  <CustomButton
                    onClick={() => setIsMapOpen(false)}
                    customeClass="gradient-button h-[36px] px-3"
                  >
                    Confirm location
                  </CustomButton>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4">
            <div className="text-sm font-semibold mb-2">Upload Documents</div>
            <div className="w-full flex gap-3 flex-wrap">
              {documents.map((doc, idx) => {
                const uploadedDoc = uploadedFiles?.find(
                  (f) => f.name === doc.name
                );
                const hasError = missingDocs.includes(doc.name);
                return (
                  <div key={idx} className="flex flex-col">
                    <UploadCard
                      doc_name={doc.name}
                      heading={doc.heading}
                      documentType={doc.documentType}
                      onFileSelect={(file) => handleFileSelect(file, doc.name)}
                      fileUploaded={!!uploadedDoc}
                      fileUrl={uploadedDoc?.fileUrl}
                      hasError={hasError}
                    />
                    {hasError && (
                      <span className="text-xs text-[#d32f2f] mt-1 ml-1">
                        {doc.name} is required.
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="grid md:grid-cols-3 gap-y-2 text-sm text-gray-700">
            <div>
              <strong>Store Name</strong>
              <div className="mt-1">{formData.storeName || "-"}</div>
            </div>
            <div>
              <strong>Store Contact Number</strong>
              <div className="mt-1">{formData.storeContactNumber || "-"}</div>
            </div>
            <div>
              <strong>License Number</strong>
              <div className="mt-1">{formData.storeLicenseNumber || "-"}</div>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-700">
            <strong>Address</strong>
            <div className="mt-1">
              {[
                formData.addressLine1,
                // formData.addressLine2,
                formData.landmark,
                formData.city,
                formData.state,
                formData.zip,
              ]
                .filter(Boolean)
                .join(", ") || "-"}
            </div>
          </div>

          <div className="mt-4">
            <div className="text-sm font-semibold mb-2">Uploaded Documents</div>
            <div className="flex gap-3 flex-wrap">
              {uploadedFiles.length > 0 &&
                uploadedFiles?.map((doc, index) => {
                  const fileName = doc.name || `Document ${index + 1}`;
                  return <FilePreview key={index} file={doc} />;
                })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
